import Vue from 'vue';
import Vuex from 'vuex';
import messages from '../assets/langs/langs';
import { localize } from 'vee-validate';
import { ApiClient, UserGetJwtTokenResponse } from '../api/api-client';
import { operations } from '@/types/types';

Vue.use(Vuex);

export type User = operations['logUser']['responses']['200']['content']['application/json'];

export interface State {
    config: { webapiurl: string };
    lang: string;
    user: User | null;
    dark: boolean;
}

const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;
const SESSION_EXPIRED_STORAGE_KEY = 'sessionExpired';
let tokenRefreshTimeout: ReturnType<typeof setTimeout> | null = null;
let tokenRefreshInFlight = false;

function clearTokenRefreshTimeout() {
    if (tokenRefreshTimeout) {
        clearTimeout(tokenRefreshTimeout);
        tokenRefreshTimeout = null;
    }
}

function scheduleTokenRefresh(expirationTimestampSecondsUtc: number, dispatch: (action: string) => void) {
    clearTokenRefreshTimeout();
    if (!expirationTimestampSecondsUtc) {
        return;
    }
    const refreshAtMs = (expirationTimestampSecondsUtc * 1000) - TOKEN_REFRESH_BUFFER_MS;
    let delayMs = Math.max(refreshAtMs - Date.now(), 0);
    const MAX_INT32 = 2 ** 31 - 1; // 2147483647
    if (delayMs > MAX_INT32) delayMs = MAX_INT32;
    tokenRefreshTimeout = setTimeout(() => {
        dispatch('refreshToken');
    }, delayMs);
}

function redirectToLoginForSessionExpired() {
    localStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, '1');
    if (window.location.hash !== '#/' && window.location.hash !== '#') {
        window.location.hash = '#/';
    }
}

const store = new Vuex.Store<State>({
    state: {
        config: {
            webapiurl: '',
        },
        lang: 'fr',
        user: null,
        dark: false
    },
    getters: {
        webapiurl: (state) => state.config.webapiurl,
        lang: (state) => state.lang,
        messages: (getters) => messages[getters.lang],
        user: (state) => state.user,
        token: (state) => state.user?.token,
        dark: (state) => state.dark,
    },
    mutations: {
        initialiseStore(state) {
            if (localStorage.getItem('store')) {
                const persistedState = JSON.parse(localStorage.getItem('store')!);
                const configFromFile = state.config;
                this.replaceState(Object.assign(state, persistedState));
                state.config = configFromFile;
            }
        },
        setLang(state, payload) {
            state.lang = payload;
        },
        setUser(state, response: User | null) {
            state.user = response;
        },
        toggleDarkMode(state) {
            state.dark = !state.dark;
        },
    },
    actions: {
        changeLang(context, lang: string) {
            context.commit('setLang', lang);
            localize(this.getters.lang);
        },
        login(context, response: User) {
            context.commit('setUser', response);
        },
        async refreshToken(context) {

            if (!context.state.user?.token || tokenRefreshInFlight) {
                return;
            }
            tokenRefreshInFlight = true;
            try {
                const api = new ApiClient({
                    BASE: context.getters.webapiurl,
                    HEADERS: {
                        Authorization: `JWT ${context.getters.token}`,
                    }
                });
                const response = await api.users.getNewJwtToken() as UserGetJwtTokenResponse;
                context.commit('setUser', {
                    ...context.state.user,
                    token: response.token,
                    application_description: response.application_description,
                    application_rights: context.state.user.application_rights,
                    token_expiration_timestamp_seconds_utc: response.token_expiration_timestamp_seconds_utc,
                });
            } catch (error) {
                context.commit('setUser', null);
                redirectToLoginForSessionExpired();
            } finally {
                tokenRefreshInFlight = false;
            }
        },
        logout(context) {
            context.commit('setUser', null);
        },
        toggleDarkMode(context) {
            context.commit('toggleDarkMode');
        },
    },
    modules: {},
});

/* ******* Update local storage each time the state is mutated ******* */
store.subscribe((mutation, state) => {
    localStorage.setItem('store', JSON.stringify(state));
    if (mutation.type === 'setUser' || mutation.type === 'initialiseStore') {
        if (state.user?.token_expiration_timestamp_seconds_utc) {
            scheduleTokenRefresh(
                state.user.token_expiration_timestamp_seconds_utc,
                store.dispatch.bind(store),
            );
        } else {
            clearTokenRefreshTimeout();
        }
    }
});

export default store;
